/**
 * =====================================================
 * FUNÇÕES DE VALIDAÇÃO DE DOCUMENTOS
 * =====================================================
 * Validação de CPF e CNPJ com feedback visual
 * =====================================================
 */

/**
 * Valida CPF
 * @param {string} cpf - CPF com ou sem formatação
 * @returns {boolean} - true se válido, false se inválido
 */
export const validateCPF = (cpf) => {
  if (!cpf) return false;

  // Remove caracteres não numéricos
  cpf = cpf.replace(/[^\d]/g, '');

  // Verifica se tem 11 dígitos
  if (cpf.length !== 11) {
    return false;
  }

  // Verifica se todos os dígitos são iguais (CPF inválido)
  if (/^(\d)\1{10}$/.test(cpf)) {
    return false;
  }

  // Validar primeiro dígito verificador
  let sum = 0;
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }

  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) {
    return false;
  }

  // Validar segundo dígito verificador
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11))) {
    return false;
  }

  return true;
};

/**
 * Valida CNPJ
 * @param {string} cnpj - CNPJ com ou sem formatação
 * @returns {boolean} - true se válido, false se inválido
 */
export const validateCNPJ = (cnpj) => {
  if (!cnpj) return false;

  // Remove caracteres não numéricos
  cnpj = cnpj.replace(/[^\d]/g, '');

  // Verifica se tem 14 dígitos
  if (cnpj.length !== 14) {
    return false;
  }

  // Verifica se todos os dígitos são iguais (CNPJ inválido)
  if (/^(\d)\1{13}$/.test(cnpj)) {
    return false;
  }

  // Validar primeiro dígito verificador
  let size = cnpj.length - 2;
  let numbers = cnpj.substring(0, size);
  const digits = cnpj.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result != digits.charAt(0)) {
    return false;
  }

  // Validar segundo dígito verificador
  size = size + 1;
  numbers = cnpj.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result != digits.charAt(1)) {
    return false;
  }

  return true;
};

/**
 * Formata CPF (xxx.xxx.xxx-xx)
 * @param {string} cpf - CPF sem formatação
 * @returns {string} - CPF formatado
 */
export const formatCPF = (cpf) => {
  if (!cpf) return '';

  cpf = cpf.replace(/[^\d]/g, '');

  if (cpf.length <= 3) return cpf;
  if (cpf.length <= 6) return cpf.replace(/(\d{3})(\d{1,3})/, '$1.$2');
  if (cpf.length <= 9) return cpf.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
};

/**
 * Formata CNPJ (xx.xxx.xxx/xxxx-xx)
 * @param {string} cnpj - CNPJ sem formatação
 * @returns {string} - CNPJ formatado
 */
export const formatCNPJ = (cnpj) => {
  if (!cnpj) return '';

  cnpj = cnpj.replace(/[^\d]/g, '');

  if (cnpj.length <= 2) return cnpj;
  if (cnpj.length <= 5) return cnpj.replace(/(\d{2})(\d{1,3})/, '$1.$2');
  if (cnpj.length <= 8) return cnpj.replace(/(\d{2})(\d{3})(\d{1,3})/, '$1.$2.$3');
  if (cnpj.length <= 12) return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{1,4})/, '$1.$2.$3/$4');
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
};

/**
 * Detecta se é CPF ou CNPJ baseado no tamanho
 * @param {string} document - Documento com ou sem formatação
 * @returns {string} - 'cpf', 'cnpj' ou 'unknown'
 */
export const detectDocumentType = (document) => {
  if (!document) return 'unknown';

  const numbers = document.replace(/[^\d]/g, '');

  if (numbers.length === 11) return 'cpf';
  if (numbers.length === 14) return 'cnpj';
  return 'unknown';
};

/**
 * Valida CPF ou CNPJ automaticamente
 * @param {string} document - Documento com ou sem formatação
 * @returns {boolean} - true se válido, false se inválido
 */
export const validateDocument = (document) => {
  const type = detectDocumentType(document);

  if (type === 'cpf') return validateCPF(document);
  if (type === 'cnpj') return validateCNPJ(document);
  return false;
};

/**
 * Formata CPF ou CNPJ automaticamente baseado no tamanho
 * @param {string} document - Documento com ou sem formatação
 * @returns {string} - Documento formatado
 */
export const formatDocument = (document) => {
  if (!document) return '';

  const numbers = document.replace(/[^\d]/g, '');

  if (numbers.length <= 11) {
    return formatCPF(numbers);
  }
  return formatCNPJ(numbers);
};

/**
 * Obtém classe CSS para feedback visual
 * @param {string} value - Valor do campo
 * @param {boolean} touched - Se o campo foi tocado
 * @returns {string} - Classes CSS para aplicar
 */
export const getValidationClass = (value, touched = true) => {
  if (!value || !touched) return '';

  const isValid = validateDocument(value);

  return isValid
    ? 'border-green-500 dark:border-green-500 focus:ring-green-500'
    : 'border-red-500 dark:border-red-500 focus:ring-red-500';
};

/**
 * Obtém mensagem de validação
 * @param {string} value - Valor do campo
 * @param {boolean} touched - Se o campo foi tocado
 * @returns {object} - {isValid, message}
 */
export const getValidationMessage = (value, touched = true) => {
  if (!value || !touched) return { isValid: null, message: '' };

  const type = detectDocumentType(value);

  if (type === 'unknown') {
    return { isValid: false, message: 'Digite um CPF ou CNPJ válido' };
  }

  const isValid = type === 'cpf' ? validateCPF(value) : validateCNPJ(value);

  return {
    isValid,
    message: isValid
      ? `${type.toUpperCase()} válido ✓`
      : `${type.toUpperCase()} inválido`
  };
};

export default {
  validateCPF,
  validateCNPJ,
  formatCPF,
  formatCNPJ,
  formatDocument,
  detectDocumentType,
  validateDocument,
  getValidationClass,
  getValidationMessage
};
